'use client'

import { cn } from '@/lib/utils'

interface LogoProps {
  variant?: 'full' | 'icon' | 'text'
  size?: 'sm' | 'md' | 'lg' | 'xl'
  className?: string
}

const sizeMap = {
  sm: { icon: 24, text: 'text-sm', gap: 'gap-1.5' },
  md: { icon: 32, text: 'text-lg', gap: 'gap-2' },
  lg: { icon: 40, text: 'text-xl', gap: 'gap-2.5' },
  xl: { icon: 56, text: 'text-2xl', gap: 'gap-3' },
}

// Open Padlock Icon - represents "taking control" / "unlocking access"
export function LogoIcon({ size = 32, className }: { size?: number; className?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role="img"
      aria-label="C4 Owner Logo"
    >
      {/* Padlock body */}
      <rect
        x="12"
        y="28"
        width="40"
        height="30"
        rx="4"
        fill="#c97c5d"
        stroke="#3d3835"
        strokeWidth="2.5"
      />
      {/* Open shackle */}
      <path
        d="M20 28V18C20 11.4 25.4 6 32 6C38.6 6 44 11.4 44 18"
        stroke="#3d3835"
        strokeWidth="4"
        strokeLinecap="round"
        fill="none"
      />
      {/* Shackle "unlocked" part tilted */}
      <path
        d="M44 18L50 12"
        stroke="#3d3835"
        strokeWidth="4"
        strokeLinecap="round"
      />
      {/* Keyhole */}
      <circle cx="32" cy="40" r="4" fill="#3d3835" />
      <rect x="30" y="42" width="4" height="10" rx="1" fill="#3d3835" />
    </svg>
  )
}

// Text component for the logo wordmark
function LogoText({ size = 'md', className }: { size?: 'sm' | 'md' | 'lg' | 'xl'; className?: string }) {
  const textClass = sizeMap[size].text
  return (
    <span className={cn('font-bold text-foreground', textClass, className)}>
      C4 Owner
    </span>
  )
}

// Main Logo component
export function Logo({ variant = 'full', size = 'md', className }: LogoProps) {
  const iconSize = sizeMap[size].icon
  const gapClass = sizeMap[size].gap

  if (variant === 'icon') {
    return <LogoIcon size={iconSize} className={className} />
  }

  if (variant === 'text') {
    return <LogoText size={size} className={className} />
  }

  // Full logo: icon + text
  return (
    <div className={cn('flex items-center', gapClass, className)}>
      <LogoIcon size={iconSize} />
      <LogoText size={size} />
    </div>
  )
}

// Default export for convenience
export default Logo
